import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Message } from "@ag-ui/client";

interface SessionArtifactsContextValue {
  names: string[];
  setNames: (names: string[]) => void;
}

// Holds the names of the artifacts the session's replies show, above both
// `Chat` (which owns the messages) and `SessionContextSidebar` (which lists
// them). Mirrors `SessionPlanProvider`.
const SessionArtifactsContext = createContext<SessionArtifactsContextValue | null>(null);

export const SessionArtifactsProvider = ({
  conversationId,
  children,
}: {
  conversationId?: string;
  children: React.ReactNode;
}) => {
  const [names, setNames] = useState<string[]>([]);

  // Reset while rendering, as `SessionPlanProvider` does, so one session's
  // artifacts do not linger over the next while its `Chat` remounts.
  const [prevConversationId, setPrevConversationId] = useState(conversationId);
  if (conversationId !== prevConversationId) {
    setPrevConversationId(conversationId);
    setNames([]);
  }

  return (
    <SessionArtifactsContext.Provider value={{ names, setNames }}>
      {children}
    </SessionArtifactsContext.Provider>
  );
};

const ARTIFACT_REF_RE = /\[\[([^\]]+)\]\]/g;

/** Every artifact an assistant reply shows with `[[name]]`, once each, in the
 *  order they first appear. */
export function artifactRefsFromMessages(messages: ReadonlyArray<Message>): string[] {
  const names = new Set<string>();
  for (const m of messages) {
    if (m.role !== "assistant" || typeof m.content !== "string") continue;
    for (const match of m.content.matchAll(ARTIFACT_REF_RE)) names.add(match[1]);
  }
  return [...names];
}

const sameNames = (a: string[], b: string[]) =>
  a.length === b.length && a.every((name, i) => name === b[i]);

// Called from `Chat`. Publishes the shown artifacts into the shared context so
// the sibling sidebar can list them.
export function useSessionArtifactsSync(messages: ReadonlyArray<Message>): void {
  const ctx = useContext(SessionArtifactsContext);
  const published = ctx?.names;
  const setNames = ctx?.setNames;
  const names = useMemo(() => artifactRefsFromMessages(messages), [messages]);

  useEffect(() => {
    if (published && setNames && !sameNames(published, names)) setNames(names);
  }, [published, setNames, names]);
}

/** The artifacts the session's replies show; empty outside a session. */
export function useSessionArtifacts(): string[] {
  return useContext(SessionArtifactsContext)?.names ?? [];
}
